/**
 * /affiliate/dashboard — approved+bound + current-terms-accepted only.
 *
 * All display data comes from server RPCs. The server:
 *   - Strips pending amounts, PII, customer identity, risk flags at the SQL
 *     layer (structurally invisible, not CSS-hidden).
 *   - Masks payout details (only last-4s), and returns null when the
 *     encryption key is missing.
 *   - Derives the share URL server-side so a rogue extension can't rewrite it.
 *
 * Direct-navigation from an ineligible state hard-redirects to /affiliate/status.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getAffiliateDashboard } from "@/lib/affiliate/dashboard.functions";
import { getMyAffiliateLedger } from "@/lib/affiliate/ledger.functions";
import { getAffiliatePublicConfig } from "@/lib/affiliate/portal.functions";
import { affiliateMyRemedies, type AffiliateRemedyRow } from "@/lib/affiliate/remedies.functions";
import {
  getAffiliateReconsentStatus,
  recordAffiliateReconsent,
} from "@/lib/affiliate/terms-admin.functions";
import { getActiveAffiliateTerms } from "@/lib/affiliate/terms.functions";
import {
  getMyPayoutRequests,
  requestAffiliatePayout,
} from "@/lib/affiliate/payout-request.functions";
import {
  payoutRequestEligibility,
  payoutRequestMessage,
} from "@/lib/affiliate/payout-request";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/affiliate/dashboard")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Affiliate dashboard · TruWe" },
      {
        name: "description",
        content: "Your TruWe Affiliate dashboard, earnings, tier progress and payouts.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AffiliateDashboardPage,
});

function rupees(paise: number): string {
  const rs = Math.round(paise) / 100;
  return "₹" + rs.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function bpsLabel(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

function AffiliateDashboardPage() {
  const navigate = useNavigate();
  const fetchDash = useServerFn(getAffiliateDashboard);
  const fetchLedger = useServerFn(getMyAffiliateLedger);
  const fetchConfig = useServerFn(getAffiliatePublicConfig);
  const fetchRemedies = useServerFn(affiliateMyRemedies);

  const [authed, setAuthed] = useState<null | boolean>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
  }, []);

  useEffect(() => {
    if (authed === false) navigate({ to: "/affiliate/login", replace: true });
  }, [authed, navigate]);

  const configQuery = useQuery({
    queryKey: ["affiliate", "public-config"],
    queryFn: () => fetchConfig(),
    staleTime: 60_000,
  });
  const dashQuery = useQuery({
    queryKey: ["affiliate", "dashboard"],
    queryFn: () => fetchDash(),
    enabled: authed === true,
  });
  const ledgerQuery = useQuery({
    queryKey: ["affiliate", "ledger"],
    queryFn: () => fetchLedger(),
    enabled: authed === true && dashQuery.data?.ok === true,
  });
  const remediesQuery = useQuery({
    queryKey: ["affiliate", "remedies"],
    queryFn: () => fetchRemedies(),
    enabled: authed === true && dashQuery.data?.ok === true,
    staleTime: 30_000,
  });

  // Route-out on ineligible states; the status page renders the correct card.
  useEffect(() => {
    if (dashQuery.data && dashQuery.data.ok === false) {
      navigate({ to: "/affiliate/status", replace: true });
    }
  }, [dashQuery.data, navigate]);

  const disabled = configQuery.data?.enabled === false;

  const tierPct = useMemo(() => {
    const d = dashQuery.data;
    if (!d || !d.ok) return 0;
    if (!d.nextTierThreshold || d.nextTierThreshold <= 0) return 100;
    return Math.min(100, Math.round((d.maturedSaleCount / d.nextTierThreshold) * 100));
  }, [dashQuery.data]);

  if (authed === null || dashQuery.isLoading) {
    return <Shell><p>Loading…</p></Shell>;
  }
  if (!dashQuery.data || dashQuery.data.ok !== true) {
    return <Shell><p>Opening your dashboard…</p></Shell>;
  }
  const d = dashQuery.data;

  return (
    <Shell>
      <header className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          TruWe Affiliate
        </p>
        <h1 className="font-display text-2xl font-semibold text-brand-deep">
          Welcome back, {d.displayName}
        </h1>
      </header>

      {disabled && (
        <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          The affiliate programme is currently paused. Data shown may be preview only.
        </p>
      )}

      <section
        aria-label="Your affiliate code"
        className="mt-6 rounded-2xl border border-border bg-card p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Your coupon code
            </p>
            <p className="mt-1 font-mono text-2xl font-semibold text-brand-deep">
              {d.couponCode}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(d.shareUrl);
                  toast.success("Share link copied");
                } catch {
                  toast.error("Couldn't copy, long-press to select the link");
                }
              }}
            >
              Copy share link
            </Button>
            <Button
              type="button"
              onClick={async () => {
                type ShareCapableNavigator = Navigator & {
                  share?: (data: ShareData) => Promise<void>;
                };
                const nav = navigator as ShareCapableNavigator;
                if (nav.share) {
                  try {
                    await nav.share({
                      title: "TruWe",
                      text: `Use code ${d.couponCode} for 10% off at TruWe.`,
                      url: d.shareUrl,
                    });
                  } catch {
                    /* user cancelled — no toast */
                  }
                } else {
                  await navigator.clipboard.writeText(d.shareUrl);
                  toast.success("Share link copied");
                }
              }}
            >
              Share
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          <span className="font-mono">{d.shareUrl}</span>
        </p>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-3">
        <StatCard
          label="Current tier"
          value={bpsLabel(d.currentTierBps)}
          hint={
            d.nextTierBps && d.nextTierThreshold
              ? `Next: ${bpsLabel(d.nextTierBps)} after ${d.nextTierThreshold} matured sales`
              : "Top tier"
          }
        />
        <StatCard label="Payable balance" value={rupees(d.payablePaise)} hint="Ready for next payout" />
        <StatCard label="Paid to date" value={rupees(d.paidPaise)} hint="Lifetime" />
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-brand-deep">Progress to next tier</p>
          <p className="text-xs text-muted-foreground">
            {d.maturedSaleCount} matured sale{d.maturedSaleCount === 1 ? "" : "s"}
          </p>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-brand-soft">
          <div
            className="h-full bg-brand-deep transition-all"
            style={{ width: `${tierPct}%` }}
            role="progressbar"
            aria-valuenow={tierPct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Only delivered, matured, non-refunded orders count.
        </p>
      </section>

      <section
        aria-label="Payout details"
        className="mt-6 rounded-2xl border border-border bg-card p-6"
      >
        <h2 className="text-sm font-medium text-brand-deep">Payout details</h2>
        {d.payout.hasDetails ? (
          <dl className="mt-3 grid gap-2 text-sm">
            <MaskRow label="PAN" value={d.payout.panLast4 ? `••••${d.payout.panLast4}` : "-"} />
            <MaskRow
              label="Bank account"
              value={
                d.payout.accountNumberLast4
                  ? `••••${d.payout.accountNumberLast4}`
                  : "-"
              }
            />
            <MaskRow label="IFSC" value={d.payout.ifscLast4 ? `••••${d.payout.ifscLast4}` : "-"} />
            {d.payout.gstinLast4 && (
              <MaskRow label="GSTIN" value={`••••${d.payout.gstinLast4}`} />
            )}
          </dl>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Add PAN + bank details to receive payouts. Minimum payout is{" "}
            {rupees(d.minimumPayoutPaise)}.
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          KYC values are encrypted at rest. Only last-4 digits are shown here.
        </p>
      </section>

      <RequestPayoutCard
        payablePaise={d.payablePaise}
        minimumPayoutPaise={d.minimumPayoutPaise}
        hasPayoutDetails={d.payout.hasDetails}
      />


      <section
        aria-label="Payable and paid ledger"
        className="mt-6 rounded-2xl border border-border bg-card p-6"
      >
        <h2 className="text-sm font-medium text-brand-deep">Earnings ledger</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Verified, payable and paid commissions are listed. Pending or held
          amounts are excluded by design.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2">Order</th>
                <th>Verified</th>
                <th>Sale (pre-GST)</th>
                <th>Commission</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(ledgerQuery.data ?? []).map((r) => (
                <tr key={r.attribution_id} className="border-t border-border/60">
                  <td className="py-2 font-mono text-xs">{r.order_reference}</td>
                  <td>{r.verified_at ? new Date(r.verified_at).toLocaleDateString() : "-"}</td>
                  <td>{rupees(r.net_eligible_sale_paise)}</td>
                  <td>{rupees(r.commission_amount_paise)}</td>
                  <td className="capitalize">
                    {r.payout_status}
                    {r.payout_status === "verified" && r.settlement_eligible_on && (
                      <span className="block text-xs normal-case text-muted-foreground">
                        payable from{" "}
                        {new Date(r.settlement_eligible_on).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(ledgerQuery.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-xs text-muted-foreground">
                    No matured earnings yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <RemediesSection rows={remediesQuery.data ?? []} />





      <section
        aria-label="ASCI reminder"
        className="mt-6 rounded-2xl border border-brand-soft bg-brand-soft/30 p-6 text-sm"
      >
        <h2 className="text-sm font-medium text-brand-deep">Disclose your partnership</h2>
        <p className="mt-1 text-muted-foreground">
          Every promotional post must carry a clear disclosure per ASCI
          influencer guidelines. Acceptable examples:
        </p>
        <p className="mt-2 font-mono text-xs">#Ad · #Sponsored · #Collaboration</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Read the{" "}
          <Link to="/policies/affiliate-terms" className="underline">
            Affiliate Partner Agreement
          </Link>{" "}
          for full disclosure duties.
        </p>
      </section>
    </Shell>
  );
}

/**
 * Partner-initiated payout request. The button is a UI convenience only —
 * `affiliate_request_payout` re-derives the balance and re-checks the minimum
 * and KYC server-side, so a tampered client cannot request more than it earned.
 */
function RequestPayoutCard({
  payablePaise,
  minimumPayoutPaise,
  hasPayoutDetails,
}: {
  payablePaise: number;
  minimumPayoutPaise: number;
  hasPayoutDetails: boolean;
}) {
  const qc = useQueryClient();
  const fetchRequests = useServerFn(getMyPayoutRequests);
  const submitRequest = useServerFn(requestAffiliatePayout);
  const [note, setNote] = useState("");

  const requestsQuery = useQuery({
    queryKey: ["affiliate", "payout-requests"],
    queryFn: () => fetchRequests(),
    staleTime: 15_000,
  });
  const rows = requestsQuery.data ?? [];
  const open = rows.find((r) => r.status === "requested") ?? null;

  const eligibility = payoutRequestEligibility({
    payablePaise,
    minimumPayoutPaise,
    hasPayoutDetails,
    hasOpenRequest: Boolean(open),
  });

  const mutation = useMutation({
    mutationFn: () => submitRequest({ data: { note: note.trim() || null } }),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success("Payout requested. We'll review it shortly.");
        setNote("");
        void qc.invalidateQueries({ queryKey: ["affiliate", "payout-requests"] });
      } else {
        toast.error(
          payoutRequestMessage(
            payoutRequestEligibility({
              payablePaise,
              minimumPayoutPaise,
              hasPayoutDetails: res.reason !== "missing_payout_details",
              hasOpenRequest: res.reason === "already_open",
            }),
          ) || "Couldn't submit the request.",
        );
        void qc.invalidateQueries({ queryKey: ["affiliate", "payout-requests"] });
      }
    },
    onError: () => toast.error("Couldn't submit the request. Please try again."),
  });

  const fmtDate = (v: string | null) => {
    if (!v) return "-";
    try {
      return new Date(v).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  return (
    <section
      aria-label="Request a payout"
      className="mt-6 rounded-2xl border border-border bg-card p-6"
    >
      <h2 className="text-sm font-medium text-brand-deep">Request a payout</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Payable balance {rupees(payablePaise)} · minimum payout{" "}
        {rupees(minimumPayoutPaise)}.
      </p>

      {open ? (
        <p className="mt-4 rounded-md border border-brand-soft bg-brand-soft/30 p-3 text-sm text-brand-deep">
          Requested {rupees(open.requested_paise)} on {fmtDate(open.created_at)} — under review.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block text-xs text-muted-foreground" htmlFor="payout-note">
            Note (optional)
          </label>
          <textarea
            id="payout-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 300))}
            maxLength={300}
            rows={2}
            placeholder="Anything we should know about this payout?"
            className="w-full rounded-md border border-border bg-background p-2 text-sm"
            disabled={!eligibility.canRequest || mutation.isPending}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={!eligibility.canRequest || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? "Requesting…" : "Request payout"}
            </Button>
            {!eligibility.canRequest && (
              <p className="text-xs text-muted-foreground">
                {payoutRequestMessage(eligibility)}
              </p>
            )}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2">Requested</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Note from TruWe</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60">
                  <td className="py-2">{fmtDate(r.created_at)}</td>
                  <td>{rupees(r.requested_paise)}</td>
                  <td className="capitalize">{r.status}</td>
                  <td className="text-muted-foreground">{r.decline_reason ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Payouts are released after the settlement window and our usual checks.
      </p>
    </section>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-xl font-semibold text-brand-deep">{value}</p>
      <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
function MaskRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="font-mono">{value}</dd>
    </div>
  );
}
function Shell({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto max-w-4xl px-4 py-10">{children}</main>;
}

// Affiliate-facing remedy notices. Shows only the notice header, response
// deadline, final decision text and appeal status returned by the
// `affiliate_my_remedies` RPC — admin notes, evidence URLs, internal risk
// scoring and pre-decision workflow state are stripped server-side.
function RemediesSection({ rows }: { rows: AffiliateRemedyRow[] }) {
  if (rows.length === 0) return null;
  const fmt = (v: string | null) => {
    if (!v) return null;
    try {
      return new Date(v).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };
  const decisionLabel = (d: string | null): string => {
    switch (d) {
      case "warning": return "Warning issued";
      case "coupon_pause": return "Coupon paused";
      case "affiliate_pause": return "Account paused";
      case "termination": return "Agreement terminated";
      case "dismissed": return "Dismissed, no action";
      default: return "Under review";
    }
  };
  return (
    <section
      aria-label="Compliance notices"
      className="mt-6 rounded-2xl border border-border bg-card p-6"
    >
      <h2 className="text-sm font-medium text-brand-deep">Compliance notices</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Notices raised under the Affiliate Partner Agreement. Only the final
        decision and any appeal status is shown here.
      </p>
      <ul className="mt-4 space-y-3">
        {rows.map((r) => {
          const noticeOn = fmt(r.notice_sent_at);
          const deadline = fmt(r.response_deadline);
          const decidedOn = fmt(r.decided_at);
          const appealedOn = fmt(r.appeal_at);
          const decided = Boolean(r.decision);
          return (
            <li
              key={r.id}
              className="rounded-xl border border-border/70 bg-background p-4 text-sm"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium text-brand-deep">
                  {r.violation_code}
                  <span className="ml-2 text-xs font-normal capitalize text-muted-foreground">
                    · {r.severity}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {decided
                    ? `Decided ${decidedOn ?? ""}`
                    : deadline
                      ? `Respond by ${deadline}`
                      : noticeOn
                        ? `Notice sent ${noticeOn}`
                        : "Open"}
                </p>
              </div>
              <p className="mt-2 text-sm">
                <span
                  className={
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium " +
                    (decided
                      ? r.decision === "dismissed"
                        ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                        : "bg-amber-50 text-amber-900 border border-amber-200"
                      : "bg-brand-soft/60 text-brand-deep border border-brand-soft")
                  }
                >
                  {decisionLabel(r.decision)}
                </span>
              </p>
              {decided && r.decision_reason && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                  {r.decision_reason}
                </p>
              )}
              {appealedOn && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Appeal recorded on {appealedOn}.
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

